/**
 * RoosterPage — Weekly schedule planning
 */

import { useEffect, useState, useCallback } from 'react';
import { format, parseISO, getWeek } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Card, Spinner } from '../components/ui';
import { WeekGrid } from '../components/rooster/WeekGrid';
import { ShiftDialog } from '../components/rooster/ShiftDialog';
import { AssignRouteDialog } from '../components/rooster/AssignRouteDialog';
import { RescheduleRouteModal } from '../components/route-planner/RescheduleRouteModal';
import { AssignShiftDialog } from '../components/rooster/AssignShiftDialog';
import { GenerateScheduleDialog } from '../components/rooster/GenerateScheduleDialog';
import { CapacityBar } from '../components/rooster/CapacityBar';
import { useScheduleStore } from '../stores/schedule-store';
import { useWorkforceStore } from '../stores';
import type { ScheduleEntry, ShiftType } from '../stores/schedule-store';
import type { RouteListItem } from '../lib/salesforce/types';
import { generateWeekSchedule, type GenerateResult } from '../lib/auto-schedule';

export function RoosterPage() {
  const {
    schedule,
    availability,
    staffList,
    sfRoutes,
    timeEntries,
    leaveEntries,
    weekStart,
    weekDates,
    isLoading,
    isSaving,
    isAssigning,
    assignError,
    teamFilter,
    editMode,
    fetchWeekData,
    goToNextWeek,
    goToPreviousWeek,
    goToThisWeek,
    addShift,
    updateShift,
    removeShift,
    addOpenShift,
    assignShift,
    assignRoute: doAssign,
    unassignRoute: doUnassign,
    toggleAvailability,
    setTeamFilter,
    setEditMode,
  } = useScheduleStore();

  // Shift dialog state
  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | null>(null);
  const [dialogStaffId, setDialogStaffId] = useState<string | null>(null);
  const [dialogDate, setDialogDate] = useState<string | null>(null);
  const [dialogShift, setDialogShift] = useState<ScheduleEntry | null>(null);

  // Route assign dialog state
  const [assignRouteDialog, setAssignRouteDialog] = useState<RouteListItem | null>(null);
  const [assignCurrentStaffId, setAssignCurrentStaffId] = useState<string | null>(null);

  // Route naar andere dag verplaatsen
  const [rescheduleRouteDialog, setRescheduleRouteDialog] = useState<RouteListItem | null>(null);

  // Open shift dialog state
  const [openShiftDialog, setOpenShiftDialog] = useState<ScheduleEntry | null>(null);

  // Auto-schedule state
  const [generateResult, setGenerateResult] = useState<GenerateResult | null>(null);
  const [showCapacity, setShowCapacity] = useState(false);

  const openRoutes = sfRoutes.filter(r => !r.chauffeurId);

  const { fetchSickLeave } = useWorkforceStore();

  useEffect(() => {
    fetchWeekData();
    fetchSickLeave();
  }, [fetchWeekData, fetchSickLeave]);

  const handleCellClick = useCallback((staffId: string, date: string) => {
    if (editMode === 'beschikbaarheid') {
      toggleAvailability(staffId, date);
      return;
    }
    setDialogStaffId(staffId);
    setDialogDate(date);
    setDialogShift(null);
    setDialogMode('add');
  }, [editMode, toggleAvailability]);

  const handleShiftClick = useCallback((shift: ScheduleEntry) => {
    setDialogStaffId(shift.staff_id);
    setDialogDate(shift.date);
    setDialogShift(shift);
    setDialogMode('edit');
  }, []);

  const handleSave = useCallback(async (shiftType: ShiftType, timeFrom: string, timeTo: string) => {
    let success = false;
    if (dialogMode === 'edit' && dialogShift) {
      success = await updateShift(dialogShift.id, { shift_type: shiftType, time_from: timeFrom, time_to: timeTo });
    } else if (dialogDate && dialogStaffId) {
      success = await addShift(dialogStaffId, dialogDate, shiftType, timeFrom, timeTo);
    } else if (dialogDate && !dialogStaffId) {
      success = await addOpenShift(dialogDate, shiftType, timeFrom, timeTo);
    }
    return success;
  }, [dialogMode, dialogShift, dialogStaffId, dialogDate, addShift, addOpenShift, updateShift]);

  const handleSaveMultiple = useCallback(async (dates: string[], shiftType: ShiftType, timeFrom: string, timeTo: string) => {
    if (!dialogStaffId) return false;
    let allOk = true;
    for (const date of dates) {
      const ok = await addShift(dialogStaffId, date, shiftType, timeFrom, timeTo);
      if (!ok) allOk = false;
    }
    return allOk;
  }, [dialogStaffId, addShift]);

  const handleDelete = useCallback(async () => {
    if (dialogShift) {
      const success = await removeShift(dialogShift.id);
      if (success) setDialogMode(null);
      return success;
    }
    return false;
  }, [dialogShift, removeShift]);

  const closeDialog = useCallback(() => {
    setDialogMode(null);
    setDialogStaffId(null);
    setDialogDate(null);
    setDialogShift(null);
  }, []);

  const handleOpenRouteClick = useCallback((route: RouteListItem) => {
    setAssignRouteDialog(route);
    setAssignCurrentStaffId(null);
  }, []);

  const handleAssignedRouteClick = useCallback((route: RouteListItem, staffId: string) => {
    setAssignRouteDialog(route);
    setAssignCurrentStaffId(staffId);
  }, []);

  const handleRouteAssign = useCallback(async (staffId: string) => {
    if (!assignRouteDialog) return;
    const success = await doAssign(assignRouteDialog.id, staffId, assignRouteDialog.datum);
    if (success) {
      setAssignRouteDialog(null);
      setAssignCurrentStaffId(null);
    }
  }, [assignRouteDialog, doAssign]);

  const handleRouteUnassign = useCallback(async () => {
    if (!assignRouteDialog) return;
    const success = await doUnassign(assignRouteDialog.id);
    if (success) {
      setAssignRouteDialog(null);
      setAssignCurrentStaffId(null);
    }
  }, [assignRouteDialog, doUnassign]);

  const closeAssignDialog = useCallback(() => {
    setAssignRouteDialog(null);
    setAssignCurrentStaffId(null);
  }, []);

  // Vanuit de assign-dialog door naar de verplaats-modal (beide triggerpunten:
  // open-route-kaart en toegewezen route-dienst openen de assign-dialog)
  const handleRouteReschedule = useCallback(() => {
    if (!assignRouteDialog) return;
    setRescheduleRouteDialog(assignRouteDialog);
    setAssignRouteDialog(null);
    setAssignCurrentStaffId(null);
  }, [assignRouteDialog]);

  // Open shift handlers
  const handleOpenCellClick = useCallback((date: string) => {
    setDialogStaffId(null);
    setDialogDate(date);
    setDialogShift(null);
    setDialogMode('add');
  }, []);

  const handleOpenShiftClick = useCallback((shift: ScheduleEntry) => {
    setOpenShiftDialog(shift);
  }, []);

  const handleOpenShiftAssign = useCallback(async (staffId: string) => {
    if (!openShiftDialog) return;
    const success = await assignShift(openShiftDialog.id, staffId);
    if (success) setOpenShiftDialog(null);
  }, [openShiftDialog, assignShift]);

  const handleOpenShiftDelete = useCallback(async () => {
    if (!openShiftDialog) return;
    const success = await removeShift(openShiftDialog.id);
    if (success) setOpenShiftDialog(null);
  }, [openShiftDialog, removeShift]);

  const handleGenerate = useCallback(() => {
    const result = generateWeekSchedule(
      staffList, weekDates, schedule, leaveEntries, availability
    );
    setGenerateResult(result);
  }, [staffList, weekDates, schedule, leaveEntries, availability]);

  const handleApplyGenerated = useCallback(async () => {
    if (!generateResult) return;
    for (const shift of generateResult.shifts) {
      await addShift(shift.staff_id, shift.date, shift.shift_type, shift.time_from, shift.time_to);
    }
    setGenerateResult(null);
  }, [generateResult, addShift]);

  const staffName = dialogStaffId
    ? staffList.find(s => s.id === dialogStaffId)?.name || ''
    : '';

  const weekNumber = getWeek(parseISO(weekStart), { weekStartsOn: 1 });
  const weekLabel = `Week ${weekNumber} — ${format(parseISO(weekDates[0] || weekStart), 'd MMM', { locale: nl })} t/m ${format(parseISO(weekDates[weekDates.length - 1] || weekStart), 'd MMM yyyy', { locale: nl })}`;

  // Count totals
  const totalShifts = schedule.length;
  const uniqueStaff = new Set(schedule.map(s => s.staff_id)).size;

  return (
    <>
      <div className="p-6 max-w-full mx-auto overflow-y-auto h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-main">Rooster</h1>
            <p className="text-muted text-sm">
              {totalShifts} diensten &middot; {uniqueStaff} medewerkers{openRoutes.length > 0 && ` \u00b7 ${openRoutes.length} open`}
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-4 mb-5">
          {/* Week navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousWeek}
              aria-label="Vorige week"
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-medium text-main min-w-[280px] text-center">
              {weekLabel}
            </span>
            <button
              onClick={goToNextWeek}
              aria-label="Volgende week"
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={goToThisWeek}
              className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              Deze week
            </button>
          </div>

          {/* Edit mode toggle */}
          <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setEditMode('rooster')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                editMode === 'rooster' ? 'bg-indigo-500 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Rooster
            </button>
            <button
              onClick={() => setEditMode('beschikbaarheid')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                editMode === 'beschikbaarheid' ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Beschikbaarheid
            </button>
          </div>

          {/* Generate + Capacity buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleGenerate}
              className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              Genereer rooster
            </button>
            <button
              onClick={() => setShowCapacity(!showCapacity)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                showCapacity ? 'bg-slate-200 text-slate-700' : 'text-slate-600 bg-slate-50 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              Capaciteit
            </button>
          </div>

          {/* Team filter */}
          <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
            {(['all', 'wasserij', 'route', 'kantoor'] as const).map(filter => (
              <button
                key={filter}
                onClick={() => setTeamFilter(filter)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  teamFilter === filter ? 'bg-indigo-500 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {filter === 'all' ? 'Iedereen' : filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Capacity bar */}
        {showCapacity && (
          <div className="mb-4">
            <CapacityBar
              staffList={staffList}
              schedule={schedule}
              leaveEntries={leaveEntries}
              availability={availability}
              weekDates={weekDates}
            />
          </div>
        )}

        {/* Grid */}
        <Card variant="elevated" padding="none">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <WeekGrid
              staff={staffList}
              schedule={schedule}
              availability={availability}
              timeEntries={timeEntries}
              leaveEntries={leaveEntries}
              weekDates={weekDates}
              teamFilter={teamFilter}
              editMode={editMode}
              onCellClick={handleCellClick}
              onShiftClick={handleShiftClick}
              openRoutes={openRoutes}
              allRoutes={sfRoutes}
              onOpenRouteClick={handleOpenRouteClick}
              onAssignedRouteClick={handleAssignedRouteClick}
              onOpenShiftClick={handleOpenShiftClick}
              onOpenCellClick={handleOpenCellClick}
            />
          )}
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-muted">
          {editMode === 'beschikbaarheid' ? (
            <>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /> Beschikbaar
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-red-100 border border-red-300" /> Niet beschikbaar
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-white border border-gray-200" /> Onbekend
              </span>
              <span className="text-slate-300">|</span>
              <span>Klik op een cel om te wisselen: beschikbaar &rarr; niet beschikbaar &rarr; onbekend</span>
            </>
          ) : (
            <>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-blue-100 border border-blue-200" /> Wasserij
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200" /> Route
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-purple-100 border border-purple-200" /> Kantoor
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /> Open dienst
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-amber-50 border border-amber-200" /> Bijrijder
              </span>
              <span className="text-slate-300">|</span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200" /> Beschikbaar
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-red-50 border border-red-200" /> Niet beschikbaar
              </span>
              <span className="flex items-center gap-1.5">
                Klik op lege cel om dienst toe te wijzen
              </span>
            </>
          )}
        </div>
      </div>

      {/* Shift dialog */}
      {dialogMode && dialogDate && (
        <ShiftDialog
          staffName={staffName}
          date={dialogDate}
          weekDates={weekDates}
          existingSchedule={dialogStaffId ? schedule.filter(s => s.staff_id === dialogStaffId) : undefined}
          existing={dialogMode === 'edit' ? dialogShift : null}
          onSave={handleSave}
          onSaveMultiple={handleSaveMultiple}
          onDelete={dialogMode === 'edit' ? handleDelete : undefined}
          onClose={closeDialog}
          isSaving={isSaving}
        />
      )}

      {/* Open shift assign dialog */}
      {openShiftDialog && (
        <AssignShiftDialog
          shift={openShiftDialog}
          staffList={staffList}
          onAssign={handleOpenShiftAssign}
          onDelete={handleOpenShiftDelete}
          onClose={() => setOpenShiftDialog(null)}
          isSaving={isSaving}
        />
      )}

      {/* Generate schedule dialog */}
      {generateResult && (
        <GenerateScheduleDialog
          result={generateResult}
          weekLabel={weekLabel}
          onApply={handleApplyGenerated}
          onClose={() => setGenerateResult(null)}
        />
      )}

      {/* Route assign dialog */}
      {assignRouteDialog && (
        <AssignRouteDialog
          route={assignRouteDialog}
          staffList={staffList}
          schedule={schedule}
          availability={availability}
          leaveEntries={leaveEntries}
          currentStaffId={assignCurrentStaffId}
          onAssign={handleRouteAssign}
          onUnassign={assignCurrentStaffId ? handleRouteUnassign : undefined}
          onReschedule={handleRouteReschedule}
          onClose={closeAssignDialog}
          isAssigning={isAssigning}
          error={assignError}
        />
      )}

      {/* Route naar andere dag verplaatsen */}
      {rescheduleRouteDialog && (
        <RescheduleRouteModal
          routeId={rescheduleRouteDialog.id}
          routeName={rescheduleRouteDialog.routeName || rescheduleRouteDialog.name}
          oldDate={rescheduleRouteDialog.datum}
          chauffeurName={rescheduleRouteDialog.chauffeurName}
          ophalenCount={rescheduleRouteDialog.stopCountOphalen}
          terugbrengenCount={rescheduleRouteDialog.stopCountAfleveren}
          locked={rescheduleRouteDialog.locked}
          onClose={() => setRescheduleRouteDialog(null)}
          onSuccess={() => fetchWeekData()}
        />
      )}
    </>
  );
}
